import { useEffect, useRef } from 'react';
import type { ExperienceChild } from '@/features/experiences/types';
import { rendersColorLegend } from '@/features/layouts/layout.lib';
import { usePlannerViewRange } from '@/features/planner/hooks/usePlannerViewRange';

type Args = {
  activeChildId: string | undefined;
  // The parent's embedded entry for the active view, which the bar always has.
  activeChild: ExperienceChild | undefined;
};

// Whether the active view paints its events with category colors, which is what
// the Key is there to decode.
//
// Deliberately not `isColorLegendVisible`: "Show color legend" decides whether a
// printed sheet spends space on a legend block, a cost the Key doesn't have — it
// stays collapsed until asked for. So on screen the Key follows the colors, and
// the toggle governs print. Category color off is still a hard no: with nothing
// painted, the Key would name distinctions the view doesn't draw.
//
// Its own hook because the answer comes from two sources with different staleness:
//
// Which layouts qualify reads `layoutId` off the parent's embedded entry, so it
// is correct on every frame. Whether the view has category color
// switched on reads the loaded child, which lags the active id by at least a frame
// on each switch — so hold the last answer while it catches up, or the Key blinks
// out and back and the bar's height jumps with it.
export const useActiveViewCategoryColors = ({
  activeChildId,
  activeChild,
}: Args): { hasCategoryColors: boolean } => {
  const { experience: loadedChild } = usePlannerViewRange();

  // The legend's layout set: the calendar grids and the printable list. The Key
  // stays off the interactive list and card layouts, which have never carried one.
  //
  // Printable views count. The Key is bar chrome that never prints, so it decodes
  // and filters the sheet on screen while the sheet keeps its own printed legend
  // under "Show color legend".
  const layoutPaintsCategories =
    activeChild !== undefined && rendersColorLegend(activeChild.layoutId);

  // undefined means the published child hasn't caught up to the active id yet.
  const resolved =
    loadedChild && loadedChild.id === activeChildId
      ? loadedChild.eventCardCategoryEnabled
      : undefined;

  // Written after commit, not during render: a discarded render would leave the
  // ref holding a value that never reached the screen, and nothing corrects it —
  // the ref is read only while the child lags, which is exactly when the branch
  // that overwrites it doesn't run.
  const lastCommitted = useRef(false);
  useEffect(() => {
    if (resolved !== undefined) {
      lastCommitted.current = resolved;
    }
  }, [resolved]);

  // This render's answer wins; the ref only covers the frames where the child
  // lags. Reading it first would lag every switch by a render.
  //
  // Once the bar renders inside ChildExperienceContext this becomes a hold across
  // its isLoading, and the id comparison goes with the bridge.
  return {
    hasCategoryColors:
      layoutPaintsCategories && (resolved ?? lastCommitted.current),
  };
};
